import AxiosService from '@core/services/app/axiosService';
import {useAuthStore} from '@core/stores/authStore';
import authHeader from '@core/services/app/authHeader';
import appConsoleService from '@core/services/utils/appConsoleService';

const DEVICE_KEY = 'app.passkey.device';            // {credentialId, username, displayName}
const OFFER_DISMISSED_KEY = 'app.passkey.offerDismissed';
const SKIP_AUTO_KEY = 'app.passkey.skipAuto';        // sessionStorage : posé à la déconnexion

/**
 * Connexion biométrique (passkey WebAuthn : Touch ID, Face ID, Windows Hello,
 * empreinte Android) — commune à toutes les marques.
 *
 * Seule la clé publique part au serveur (/api/appPasskey/*) ; l'appareil garde
 * la clé privée et vérifie la biométrie. Côté navigateur on mémorise
 * uniquement l'ID de la clé + le nom de l'utilisateur, pour rouvrir la
 * boîte de login directement en mode « Se connecter avec Windows Hello ».
 */
class AppPasskeyService {

  // =========================================================
  // Capacités du navigateur / de l'appareil
  // =========================================================

  isSupported() {
    return typeof window !== 'undefined'
      && typeof window.PublicKeyCredential === 'function'
      && typeof navigator.credentials?.get === 'function';
  }

  /** Authentificateur biométrique intégré disponible (pas seulement une clé USB). */
  async isAvailable() {
    if (!this.isSupported()) return false;
    try {
      return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    } catch (e) {
      return false;
    }
  }

  /** Le navigateur sait proposer les passkeys dans l'autocomplétion du champ identifiant. */
  async supportsConditionalUI() {
    if (!this.isSupported()) return false;
    try {
      return typeof window.PublicKeyCredential.isConditionalMediationAvailable === 'function'
        && await window.PublicKeyCredential.isConditionalMediationAvailable();
    } catch (e) {
      return false;
    }
  }

  /** Nom de la biométrie de l'appareil, pour les libellés de boutons. */
  label() {
    const ua = (navigator.userAgent || '').toLowerCase();
    if (/iphone|ipad|ipod/.test(ua)) return /iphone|ipod/.test(ua) ? 'Face ID' : 'Touch ID';
    if (ua.includes('macintosh') || ua.includes('mac os')) return 'Touch ID';
    if (ua.includes('windows')) return 'Windows Hello';
    if (ua.includes('android')) return "l'empreinte";
    return 'la biométrie';
  }

  // =========================================================
  // Appareil mémorisé (localStorage, propre à chaque marque/origine)
  // =========================================================

  knownDevice() {
    try {
      const raw = localStorage.getItem(DEVICE_KEY);
      const d = raw ? JSON.parse(raw) : null;
      return d?.credentialId ? d : null;
    } catch (e) {
      return null;
    }
  }

  rememberDevice(credentialId, user) {
    try {
      localStorage.setItem(DEVICE_KEY, JSON.stringify({
        credentialId,
        username: user?.username || null,
        displayName: user?.firstName || user?.firstname || user?.username || null,
      }));
    } catch (e) { /* stockage indisponible : la biométrie reste accessible via le bouton */ }
  }

  forgetDevice() {
    try { localStorage.removeItem(DEVICE_KEY); } catch (e) { /* ignore */ }
  }

  /**
   * La clé mémorisée existe-t-elle encore côté serveur ? Si elle a été révoquée
   * (depuis un autre appareil), on l'oublie et on le signale au gestionnaire de
   * passkeys de l'OS. Serveur injoignable ⇒ on suppose active.
   */
  async isKnownDeviceActive() {
    const device = this.knownDevice();
    if (!device) return false;
    try {
      const res = await AxiosService.post('/appPasskey/status', { credentialId: device.credentialId });
      if (res?.data?.active) return true;
      this.signalRemoved(device.credentialId);
      this.forgetDevice();
      return false;
    } catch (e) {
      return true;
    }
  }

  /** Pas de lancement automatique de la biométrie juste après une déconnexion. */
  markLoggedOut() {
    try { sessionStorage.setItem(SKIP_AUTO_KEY, '1'); } catch (e) { /* ignore */ }
  }

  consumeSkipAuto() {
    try {
      const skip = sessionStorage.getItem(SKIP_AUTO_KEY) === '1';
      sessionStorage.removeItem(SKIP_AUTO_KEY);
      return skip;
    } catch (e) {
      return false;
    }
  }

  // =========================================================
  // Connexion
  // =========================================================

  /**
   * Connexion biométrique. Ouvre la session (authStore) et mémorise l'appareil.
   * @param {Object} opts
   * @param {boolean} opts.conditional  autocomplétion du champ identifiant (attend que l'utilisateur choisisse une passkey)
   * @param {AbortSignal} opts.signal   pour annuler une attente conditionnelle
   * @returns {Promise<Object>} l'utilisateur connecté
   */
  async login({ conditional = false, signal } = {}) {
    const device = conditional ? null : this.knownDevice();
    const start = await AxiosService.post('/appPasskey/loginOptions', { credentialId: device?.credentialId || null });
    const { challengeId, publicKey } = start.data;

    const request = { publicKey: this.decodeRequestOptions(publicKey) };
    if (conditional) request.mediation = 'conditional';
    if (signal) request.signal = signal;
    const assertion = await navigator.credentials.get(request);
    if (!assertion) throw Object.assign(new Error('cancelled'), { name: 'NotAllowedError' });

    let res;
    try {
      res = await AxiosService.post('/appPasskey/login', { challengeId, credential: this.encodeAssertion(assertion) });
    } catch (e) {
      if (e?.response?.status === 401 && device?.credentialId === assertion.id) {
        this.signalRemoved(assertion.id);
        this.forgetDevice();
      }
      throw e;
    }
    const user = res?.data?.user || res?.data;
    if (!user?.access_token) throw new Error('Réponse de connexion invalide');
    this.rememberDevice(assertion.id, user);
    await useAuthStore().login(user);
    return user;
  }

  // =========================================================
  // Enrôlement & gestion (utilisateur connecté)
  // =========================================================

  /** Active la biométrie sur cet appareil pour l'utilisateur connecté. */
  async register() {
    const start = await AxiosService.post('/appPasskey/registerOptions', {}, { headers: authHeader() });
    const { challengeId, publicKey } = start.data;
    const credential = await navigator.credentials.create({ publicKey: this.decodeCreationOptions(publicKey) });
    if (!credential) throw Object.assign(new Error('cancelled'), { name: 'NotAllowedError' });

    const res = await AxiosService.post('/appPasskey/register',
      { challengeId, credential: this.encodeCredential(credential) },
      { headers: authHeader() });
    this.rememberDevice(credential.id, useAuthStore().currentUser);
    return res.data.passkey;
  }

  async list() {
    const res = await AxiosService.post('/appPasskey/list', {}, { headers: authHeader() });
    return res.data.passkeys || [];
  }

  async remove(id) {
    const res = await AxiosService.post('/appPasskey/remove', { id }, { headers: authHeader() });
    const credentialId = res.data?.credentialId;
    if (credentialId) {
      this.signalRemoved(credentialId);
      if (this.knownDevice()?.credentialId === credentialId) this.forgetDevice();
    }
    return credentialId;
  }

  /** Cette clé est-elle celle de l'appareil courant ? */
  isThisDevice(passkey) {
    return !!passkey?.credentialId && this.knownDevice()?.credentialId === passkey.credentialId;
  }

  // =========================================================
  // Proposition d'activation après connexion par mot de passe
  // =========================================================

  async shouldOfferEnrollment(username) {
    if (this.knownDevice() || !(await this.isAvailable())) return false;
    try {
      const dismissed = JSON.parse(localStorage.getItem(OFFER_DISMISSED_KEY) || '[]');
      return !dismissed.includes(username);
    } catch (e) {
      return true;
    }
  }

  dismissOffer(username) {
    try {
      const dismissed = JSON.parse(localStorage.getItem(OFFER_DISMISSED_KEY) || '[]');
      if (!dismissed.includes(username)) dismissed.push(username);
      localStorage.setItem(OFFER_DISMISSED_KEY, JSON.stringify(dismissed));
    } catch (e) { /* ignore */ }
  }

  // =========================================================
  // Erreurs
  // =========================================================

  /** Annulation par l'utilisateur (ou délai dépassé) : à ignorer silencieusement. */
  isCancelled(e) {
    return e?.name === 'NotAllowedError' || e?.name === 'AbortError';
  }

  explain(e) {
    if (e?.name === 'InvalidStateError') {
      return 'Une clé existe déjà sur cet appareil pour ce compte. Supprimez-la des réglages de votre appareil (ou depuis cette liste) puis réessayez.';
    }
    if (this.isCancelled(e)) return 'Opération annulée.';
    if (e?.name === 'SecurityError') return "La biométrie n'est pas disponible sur cette adresse.";
    return e?.response?.data?.message || "La connexion biométrique a échoué. Utilisez votre mot de passe.";
  }

  /** Indique au gestionnaire de passkeys de l'OS qu'une clé n'existe plus (API Signal, best-effort). */
  signalRemoved(credentialId) {
    try {
      if (typeof window.PublicKeyCredential?.signalUnknownCredential === 'function') {
        window.PublicKeyCredential.signalUnknownCredential({ rpId: window.location.hostname, credentialId })
          .catch(() => {});
      }
    } catch (e) {
      appConsoleService.error('AppPasskeyService.signalRemoved', e);
    }
  }

  // =========================================================
  // Encodage base64url ↔ ArrayBuffer
  // =========================================================

  b64uToBuf(b64u) {
    if (!b64u) return new ArrayBuffer(0);
    const b64 = b64u.replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4 ? 4 - (b64.length % 4) : 0;
    const raw = atob(b64 + '='.repeat(pad));
    const buf = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) buf[i] = raw.charCodeAt(i);
    return buf.buffer;
  }

  bufToB64u(buf) {
    const bytes = new Uint8Array(buf);
    let raw = '';
    for (let i = 0; i < bytes.length; i++) raw += String.fromCharCode(bytes[i]);
    return btoa(raw).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  decodeCreationOptions(opts) {
    return {
      ...opts,
      challenge: this.b64uToBuf(opts.challenge),
      user: { ...opts.user, id: this.b64uToBuf(opts.user.id) },
      excludeCredentials: (opts.excludeCredentials || []).map(c => ({ ...c, id: this.b64uToBuf(c.id) })),
    };
  }

  decodeRequestOptions(opts) {
    return {
      ...opts,
      challenge: this.b64uToBuf(opts.challenge),
      allowCredentials: (opts.allowCredentials || []).map(c => ({ ...c, id: this.b64uToBuf(c.id) })),
    };
  }

  encodeCredential(cred) {
    return JSON.stringify({
      id: cred.id,
      type: cred.type,
      rawId: this.bufToB64u(cred.rawId),
      response: {
        clientDataJSON: this.bufToB64u(cred.response.clientDataJSON),
        attestationObject: this.bufToB64u(cred.response.attestationObject),
        transports: cred.response.getTransports?.() || [],
      },
      clientExtensionResults: cred.getClientExtensionResults?.() || {},
    });
  }

  encodeAssertion(assertion) {
    return JSON.stringify({
      id: assertion.id,
      type: assertion.type,
      rawId: this.bufToB64u(assertion.rawId),
      response: {
        clientDataJSON: this.bufToB64u(assertion.response.clientDataJSON),
        authenticatorData: this.bufToB64u(assertion.response.authenticatorData),
        signature: this.bufToB64u(assertion.response.signature),
        userHandle: assertion.response.userHandle ? this.bufToB64u(assertion.response.userHandle) : null,
      },
      clientExtensionResults: assertion.getClientExtensionResults?.() || {},
    });
  }
}

export default new AppPasskeyService();
